import React from 'react';
import { Series } from '@cambridgeassessment/lib-ee-commons';
import { Button, Dropdown, Subheader, TextField, Title } from '@cambridgeassessment/cambridge-ui';
import { SelectTitleWrapper, StyledSelectsRow } from './ManageSampleChanges.styles';
import { SampleSearchParams } from '../../../store/samplesManagement/types';
import { StyledInputContainer } from '../Content.styles';
import { SeriesData } from '../../../types/Series';
import SampleRerunReport from './RerunSampleReport/RerunSampleReport';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import thunks from '../../../store/series/thunks';

interface Props {
  businessUnitId: string;
  seriesData: SeriesData | undefined;
  inputsStates: SampleSearchParams;
  setInputsStates: (value: SampleSearchParams) => void;
  onSearchClick: () => void;
  onDownloadClick: () => void;
}

export const SearchInputs = (props: Props): JSX.Element => {
  const dispatch = useDispatch();
  const { seriesData, inputsStates, setInputsStates, onSearchClick, onDownloadClick } = props;
  const {
    series: { seriesComponentsDates },
    userData: { businessUnit },
  } = useSelector((state: RootState) => state);

  const allowedCharsRegExp = /^[a-zA-Z0-9]*$/;

  const onChange = (value: string, onValueChange: (value: string) => void): void => {
    if (allowedCharsRegExp.test(value)) {
      onValueChange(value);
    }
  };

  const renderSeries = (item: Series): string => item.name;
  const onSeriesSelect = (series: Series): void => {
    setInputsStates({ ...inputsStates, series: series });
    if (businessUnit) {
      dispatch(thunks.getSeriesComponentDates(businessUnit.id, series.id));
    }
  };
  const onSyllabusChange = (value: string): void => setInputsStates({ ...inputsStates, assessmentCode: value });
  const onComponentChange = (value: string): void => setInputsStates({ ...inputsStates, componentNumber: value });
  const onCentreChange = (value: string): void => setInputsStates({ ...inputsStates, centreId: value });

  const isBtnDisabled =
    !inputsStates.centreId || !inputsStates.componentNumber || !inputsStates.series || !inputsStates.assessmentCode;

  return (
    <>
      <StyledInputContainer>
        <Title id='series'>Select series</Title>
        <Dropdown<Series>
          data={(seriesData as Series[]) || []}
          renderOption={renderSeries}
          renderSelectedOption={renderSeries}
          dataQaId='series-select'
          onChange={onSeriesSelect}
          value={inputsStates.series ?? null}
          placeholder='Please select'
          title='Select series'
        />
      </StyledInputContainer>
      <SampleRerunReport
        salgoStartDate={seriesComponentsDates?.salgoStartDate ?? ''}
        bufferDate={seriesComponentsDates?.seriesBufferDate ?? ''}
        onDownloadClick={onDownloadClick}
      />
      <Title data-qa-id='manage-sample-component-search-title'>Search for components</Title>
      <StyledSelectsRow>
        <StyledInputContainer>
          <SelectTitleWrapper>
            <Subheader>Syllabus</Subheader>
          </SelectTitleWrapper>
          <TextField
            dataQaId='search-syllabus'
            onChange={(e) => onChange(e, onSyllabusChange)}
            placeholder='Syllabus number'
            value={inputsStates?.assessmentCode ?? ''}
          />
        </StyledInputContainer>
        <StyledInputContainer>
          <SelectTitleWrapper>
            <Subheader>Component</Subheader>
          </SelectTitleWrapper>
          <TextField
            dataQaId='search-component'
            onChange={(e) => onChange(e, onComponentChange)}
            placeholder='Component number'
            value={inputsStates?.componentNumber ?? ''}
          />
        </StyledInputContainer>
      </StyledSelectsRow>
      <Title data-qa-id='manage-sample-centre-search-title'>Search for centres</Title>
      <SelectTitleWrapper>
        <Subheader data-qa-id='manage-sample-component-search-subheader'>
          If a centre is within a consortium, the sample for the consortium will be returned.
        </Subheader>
      </SelectTitleWrapper>
      <StyledInputContainer>
        <SelectTitleWrapper>
          <Subheader>Centre number</Subheader>
        </SelectTitleWrapper>
        <TextField
          dataQaId='search-centre'
          onChange={(e) => onChange(e, onCentreChange)}
          placeholder='Centre number'
          value={inputsStates?.centreId ?? ''}
        />
      </StyledInputContainer>
      <Button qaId='search-button' onClick={onSearchClick} disabled={isBtnDisabled}>
        Search
      </Button>
    </>
  );
};
